import { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import { ArrowLeft, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Panel, Pill, urgTone } from "./OfficeLayout";
import { PIPELINE } from "./Leads";
import { SituationReport } from "../../components/joe/SituationReport";
import { api, errMsg, money, titleize, shortDate } from "../../lib/api";

const SPEC_STAGES = PIPELINE.slice(PIPELINE.indexOf("SPECIALIST_CONTACTED"), PIPELINE.indexOf("RESOLVED_CLOSED") + 1);
const COMP = ["NOT_APPLICABLE", "PENDING_COMPLIANCE_REVIEW", "APPROVED", "NOT_COMPENSABLE", "PAID"];
const Sel = ({ value, onChange, opts, testId }) => (
  <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-8 w-full rounded border border-border bg-background px-2 text-xs">
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const KV = ({ k, v }) => <div className="flex justify-between gap-2 border-b border-border py-1 text-xs"><span className="text-muted-foreground">{k}</span><span className="text-right font-mono">{v ?? "—"}</span></div>;

const Controls = ({ l, admin, specs, patch }) => (
  <Panel title="Case controls" testId="case-controls">
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
      <label className="text-xs text-muted-foreground">Pipeline stage
        <Sel testId="ctl-stage" value={l.stage} onChange={(v) => patch({ stage: v })} opts={(admin ? PIPELINE : [l.stage, ...SPEC_STAGES.filter((s) => s !== l.stage)]).map((s) => [s, titleize(s)])} /></label>
      {admin && <>
        <label className="text-xs text-muted-foreground">Assigned specialist
          <Sel testId="ctl-specialist" value={l.assigned_specialist_id} onChange={(v) => v && patch({ assigned_specialist_id: v })} opts={[["", "Unassigned"], ...specs.map((s) => [s.id, `${s.name} — ${titleize(s.specialist_category)}`])]} /></label>
        <label className="text-xs text-muted-foreground">Case status
          <Sel testId="ctl-case-status" value={l.case_status} onChange={(v) => patch({ case_status: v })} opts={["OPEN", "ON_HOLD", "CLOSED"].map((s) => [s, titleize(s)])} /></label>
        <label className="text-xs text-muted-foreground">Conversion status
          <Sel testId="ctl-conversion" value={l.conversion_status} onChange={(v) => patch({ conversion_status: v })} opts={["NOT_CONVERTED", "CONVERTED", "LOST"].map((s) => [s, titleize(s)])} /></label>
        <label className="text-xs text-muted-foreground">Compensation status
          <Sel testId="ctl-compensation" value={l.compensation_status} onChange={(v) => patch({ compensation_status: v })} opts={COMP.map((s) => [s, titleize(s)])} /></label>
        <label className="text-xs text-muted-foreground">Revenue attribution ($)
          <Input data-testid="ctl-revenue" defaultValue={l.revenue || ""} className="h-8 text-xs" onBlur={(e) => e.target.value && patch({ revenue: Number(e.target.value) })} /></label>
      </>}
    </div>
  </Panel>
);

const Attribution = ({ l, admin, reload }) => {
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const go = async () => {
    try { await api.post(`/office/leads/${l.id}/attribution`, { link_code: code, reason }); toast.success("Attribution updated (history preserved)"); setCode(""); setReason(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const r = l.referral;
  return (
    <Panel title="Referral attribution" testId="attribution-panel">
      <KV k="Referral code" v={r.code} /><KV k="Partner" v={r.partner_id} /><KV k="Campaign" v={r.campaign_id} /><KV k="QR" v={r.qr_id} />
      <KV k="Asset" v={r.asset_id} /><KV k="Channel" v={r.channel} /><KV k="Source" v={titleize(r.source)} /><KV k="City / ZIP" v={`${r.city || "—"} ${r.zip || ""}`} />
      <KV k="First seen" v={shortDate(r.first_seen)} /><KV k="Session" v={r.visit_id} />
      <p className="mt-3 font-mono text-[10px] uppercase text-muted-foreground">Immutable history</p>
      <div className="mt-1 space-y-1">{l.attribution_history.map((h, i) => (
        <div key={i} className="rounded border border-border p-2 text-xs" data-testid={`attr-history-${i}`}>
          <Pill tone={h.event === "ORIGINAL" ? "green" : "yellow"}>{h.event}</Pill> <span className="font-mono">{h.referral.code}</span> • {h.by} • {shortDate(h.at)}
          {h.previous && <span className="text-muted-foreground"> (from {h.previous.code})</span>}<p className="text-muted-foreground">{h.reason}</p>
        </div>))}</div>
      {admin && (
        <div className="mt-3 space-y-2">
          <Input data-testid="attr-code-input" placeholder="New tracking code" value={code} onChange={(e) => setCode(e.target.value)} className="h-8 text-xs" />
          <Input data-testid="attr-reason-input" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-8 text-xs" />
          <button data-testid="attr-reassign-btn" onClick={go} className="h-8 w-full rounded bg-amber-500/90 text-xs font-bold text-slate-950">Reassign attribution</button>
        </div>)}
    </Panel>
  );
};

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useOutletContext();
  const admin = user.role === "admin";
  const [l, setL] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [note, setNote] = useState("");
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/office/leads/${id}`).then(({ data }) => setL(data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); if (admin) api.get("/office/specialists").then(({ data }) => setSpecs(data)); }, [load, admin]);
  if (err && !l) return <div data-testid="lead-detail-error" className="space-y-2"><p className="text-sm text-red-300">{err}</p><Link to="/office/leads" className="text-xs text-primary">Back to leads</Link></div>;
  if (!l) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const an = l.analysis;
  const patch = async (body) => { try { await api.patch(`/office/leads/${id}`, body); toast.success("Saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/office/leads/${id}/notes`, { text: note }); setNote(""); load(); };
  const openDoc = async (d) => {
    try { const res = await api.get(`/office/leads/${id}/documents/${d.id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch (e) { toast.error("Unable to open document"); }
  };
  const interested = l.answers?.interested_option?.value;

  return (
    <div className="space-y-4" data-testid="lead-detail">
      <Link to="/office/leads" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Back</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-2xl font-bold" data-testid="lead-detail-id">{l.id}</h1>
        <Pill tone={urgTone(an.urgency.color)}>{an.urgency.color} {an.urgency.label}{an.urgency.days_remaining !== null ? ` • ${an.urgency.days_remaining}d` : ""}</Pill>
        <Pill tone="cyan">{titleize(l.stage)}</Pill><Pill>Legal: {an.scorecard.legal_review_priority}</Pill>
        {l.duplicate && <Pill tone="yellow" >Possible existing lead: {l.duplicate.matches.join(", ")}</Pill>}
        {an.scam.detected && <Pill tone="red">Consumer-protection issue</Pill>}
      </div>
      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Panel title="Homeowner & preferences" testId="homeowner-panel">
              <KV k="Name" v={l.contact?.name} /><KV k="Phone" v={l.contact?.phone} /><KV k="Email" v={l.contact?.email} /><KV k="Preferred contact" v={l.contact?.preferred_method} />
              <KV k="Best time" v={l.contact?.best_time} /><KV k="Option of interest" v={interested?.code ? titleize(interested.code) : "—"} />
              <KV k="Assigned specialist" v={l.specialist?.name} /><KV k="Progress" v={`${l.progress || 0}%`} />
            </Panel>
            <Panel title="Financial snapshot (estimates)" testId="financial-panel">
              <KV k="Property value" v={money(an.equity.value)} /><KV k="Total debts" v={money(an.equity.total_debts)} /><KV k="Gross equity" v={money(an.equity.gross)} />
              <KV k="Net sale equity" v={money(an.equity.net)} /><KV k="Equity confidence" v={`${an.equity.confidence}/100`} />
              <KV k="Monthly surplus" v={an.cashflow.known ? money(an.cashflow.surplus) : "—"} /><KV k="Foreclosure stage" v={an.foreclosure_stage_label} /><KV k="Tax status" v={titleize(an.tax_status)} />
            </Panel>
          </div>
          <Panel title="AI-generated questions the specialist should ask next" testId="specialist-questions">
            <ol className="list-decimal space-y-1 pl-5 text-sm">{an.specialist_questions.map((q) => <li key={q}>{q}</li>)}</ol>
          </Panel>
          <div className="grid gap-4 md:grid-cols-2">
            <Panel title="Contradictions & verification flags" testId="verification-flags">
              {an.conflicts.map((c) => <p key={c.id} className="mb-2 text-xs"><Pill tone={c.resolved ? "green" : "orange"}>{c.resolved ? `Resolved: ${titleize(c.resolution)}` : "Open"}</Pill> {c.message}</p>)}
              {an.verification.map((v) => <KV key={v.field} k={v.label} v={titleize(v.status)} />)}
            </Panel>
            <Panel title="Documents" testId="documents-panel">
              {l.documents_restricted ? <p className="text-xs text-amber-300">Homeowner has not consented to document sharing.</p> :
                l.documents.map((d) => (
                  <button key={d.id} data-testid={`office-doc-${d.id}`} onClick={() => openDoc(d)} className="mb-1 flex w-full items-center gap-2 rounded border border-border p-2 text-left text-xs hover:border-primary">
                    <FileText className="h-3.5 w-3.5" /> {titleize(d.doc_type)} <span className="truncate text-muted-foreground">{d.filename}</span></button>))}
              <p className="mt-2 font-mono text-[10px] uppercase text-muted-foreground">Missing</p>
              <p className="text-xs">{an.docs_missing_labels.join(", ") || "None"}</p>
            </Panel>
          </div>
          <Panel title="Situation report"><SituationReport r={l.report} /></Panel>
        </div>
        <div className="space-y-4">
          <Controls l={l} admin={admin} specs={specs} patch={patch} />
          <Attribution l={l} admin={admin} reload={load} />
          <Panel title="Consents" testId="consents-panel">
            {(l.consents || []).map((c, i) => <KV key={i} k={`${titleize(c.type)} (${c.source})`} v={`${c.granted ? "GRANTED" : "NO"} ${shortDate(c.at)}`} />)}
          </Panel>
          <Panel title="Notes & activity" testId="notes-panel">
            <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a call log or note" className="text-xs" />
            <button data-testid="note-add-btn" onClick={addNote} className="mt-2 h-8 w-full rounded bg-primary text-xs font-bold text-primary-foreground">Add note</button>
            {(l.notes || []).slice().reverse().map((n) => <p key={n.id} className="mt-2 border-t border-border pt-2 text-xs"><span className="text-muted-foreground">{n.by} • {shortDate(n.at)}</span><br />{n.text}</p>)}
            <p className="mt-3 font-mono text-[10px] uppercase text-muted-foreground">Stage history</p>
            {(l.stage_history || []).map((s, i) => <p key={i} className="text-[11px] text-muted-foreground">{titleize(s.stage)} • {s.by} • {shortDate(s.at)}</p>)}
          </Panel>
        </div>
      </div>
    </div>
  );
}
